import { Box, Button, Divider, IconButton, Typography } from '@mui/material';
import { useRecoilValue } from 'recoil';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { frontendSettingsAtom } from '../../../atoms/FrontendSettings';
import { PopularCategory } from '../../../interfaces/Category/PopularCategory';
import { useThemeColor } from '../hooks/useThemeColor';
import { getPopularCategories } from '../../../api/PopularCategory';
import { NavMenuItem } from '../../../interfaces/Settings/FrontendSettings';
import { bodyWidth } from '../constants/Style';
import { EditableStyleWrapper } from '../StyleEditor/EditableStyleWrapper';

const maxColWidth = 250;

const PopularCategoriesMenu = () => {
  const settings = useRecoilValue(frontendSettingsAtom);

  const [popularCategories, setPopularCategories] = useState<PopularCategory[]>(
    [],
  );

  const { secondary } = useThemeColor();

  useEffect(() => {
    getPopularCategories()
      .then((p) => setPopularCategories(p))
      .catch((err) => toast.error(err.message));
  }, []);

  return popularCategories.length ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <EditableStyleWrapper path='footerDesktopSettings.popularCategoriesTitleStyle'>
        <Typography
          sx={{
            color: secondary,
            fontSize: '18px',
            fontWeight: 600,
            mb: 1,
            ...settings.settingsV2?.footerDesktopSettings
              ?.popularCategoriesTitleStyle,
          }}
        >
          {settings.settingsV1.popular_categories_text}
        </Typography>
      </EditableStyleWrapper>

      {popularCategories.map((category) => (
        <EditableStyleWrapper
          key={category.id}
          path='footerDesktopSettings.popularCategoriesItemStyle'
        >
          <Button
            sx={{
              color: secondary,
              fontSize: '14px',
              fontWeight: 600,
              width: 'fit-content',
              p: 0,
              minWidth: 0,
              textTransform: 'none',
              textAlign: 'left',
              ...settings.settingsV2?.footerDesktopSettings
                ?.popularCategoriesItemStyle,
            }}
          >
            {category.name}
          </Button>
        </EditableStyleWrapper>
      ))}
    </Box>
  ) : null;
};

type FooterMenuProps = {
  title: string;
  menuItems: NavMenuItem[];
};

const FooterMenu = ({ title, menuItems }: FooterMenuProps) => {
  const { secondary } = useThemeColor();

  const settings = useRecoilValue(frontendSettingsAtom);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      <EditableStyleWrapper path='footerDesktopSettings.menuTitleStyle'>
        <Typography
          sx={{
            color: secondary,
            fontSize: '18px',
            fontWeight: 600,
            mb: 1,
            ...settings.settingsV2?.footerDesktopSettings?.menuTitleStyle,
          }}
        >
          {title}
        </Typography>
      </EditableStyleWrapper>

      {menuItems.map((item) => (
        <EditableStyleWrapper
          key={item.id}
          path='footerDesktopSettings.menuItemStyle'
        >
          <Button
            sx={{
              color: secondary,
              fontSize: '14px',
              fontWeight: 600,
              width: 'fit-content',
              p: 0,
              minWidth: 0,
              textTransform: 'none',
              textAlign: 'left',
              ...settings.settingsV2?.footerDesktopSettings?.menuItemStyle,
            }}
          >
            {item.name}
          </Button>
        </EditableStyleWrapper>
      ))}
    </Box>
  );
};

export const FooterDesktop = () => {
  const settings = useRecoilValue(frontendSettingsAtom);

  const { primary, secondary } = useThemeColor();

  const logo = settings.settingsV1.storefront_header_logo;

  const textStyles = {
    color: secondary,
    fontSize: '14px',
    fontWeight: 600,
  };

  const secondAddressLine = [
    settings.storeSettings.store_address_2,
    settings.storeSettings.store_city,
    settings.storeSettings.store_country,
  ].filter(Boolean);

  return (
    <EditableStyleWrapper path='footerDesktopSettings.rootStyle'>
      <Box
        sx={{
          width: '100%',
          pt: 5,
          pb: 2,
          backgroundColor: primary,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.5,
          mt: 'auto',
          ...settings.settingsV2?.footerDesktopSettings?.rootStyle,
        }}
      >
        <Box
          width={bodyWidth}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              maxWidth: maxColWidth,
              alignItems: 'start',
            }}
          >
            {logo ? (
              <EditableStyleWrapper path='footerDesktopSettings.logoStyle'>
                <Box
                  component='img'
                  src={logo}
                  sx={{
                    cursor: 'pointer',
                    height: 80,
                    width: 'auto',
                    ...settings.settingsV2?.footerDesktopSettings?.logoStyle,
                  }}
                />
              </EditableStyleWrapper>
            ) : (
              <Typography sx={{ color: secondary }}>
                {settings.settingsV1.storefront_navbar_text}
              </Typography>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {!!settings.storeSettings.store_address_1 && (
                <EditableStyleWrapper path='footerDesktopSettings.addressStyle'>
                  <Typography
                    sx={{
                      ...textStyles,
                      ...settings.settingsV2?.footerDesktopSettings
                        ?.addressStyle,
                    }}
                  >
                    {settings.storeSettings.store_address_1}
                  </Typography>
                </EditableStyleWrapper>
              )}

              {!!secondAddressLine.length && (
                <EditableStyleWrapper path='footerDesktopSettings.addressStyle'>
                  <Typography
                    sx={{
                      ...textStyles,
                      ...settings.settingsV2?.footerDesktopSettings
                        ?.addressStyle,
                    }}
                  >
                    {secondAddressLine.join(', ')}
                  </Typography>
                </EditableStyleWrapper>
              )}
            </Box>

            <Box>
              {!!settings.storeSettings.store_phone && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <EditableStyleWrapper path='footerDesktopSettings.phoneIconStyle'>
                    <PhoneIcon
                      fontSize='small'
                      sx={{
                        color: secondary,
                        ...settings.settingsV2?.footerDesktopSettings
                          ?.phoneIconStyle,
                      }}
                    />
                  </EditableStyleWrapper>
                  <EditableStyleWrapper path='footerDesktopSettings.phoneStyle'>
                    <Typography
                      sx={{
                        ...textStyles,
                        ...settings.settingsV2?.footerDesktopSettings
                          ?.phoneStyle,
                      }}
                    >
                      {settings.storeSettings.store_phone}
                    </Typography>
                  </EditableStyleWrapper>
                </Box>
              )}

              {!!settings.storeSettings.store_email && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <EditableStyleWrapper path='footerDesktopSettings.emailIconStyle'>
                    <EmailIcon
                      fontSize='small'
                      sx={{
                        color: secondary,
                        ...settings.settingsV2?.footerDesktopSettings
                          ?.emailIconStyle,
                      }}
                    />
                  </EditableStyleWrapper>
                  <EditableStyleWrapper path='footerDesktopSettings.emailStyle'>
                    <Typography
                      sx={{
                        ...textStyles,
                        ...settings.settingsV2?.footerDesktopSettings
                          ?.emailStyle,
                      }}
                    >
                      {settings.storeSettings.store_email}
                    </Typography>
                  </EditableStyleWrapper>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'row' }}>
              {!!settings.settingsV1.storefront_facebook_link && (
                <EditableStyleWrapper path='footerDesktopSettings.socialMediaStyle'>
                  <IconButton>
                    <FacebookIcon
                      sx={{
                        color: secondary,
                        ...settings.settingsV2?.footerDesktopSettings
                          ?.socialMediaStyle,
                      }}
                    />
                  </IconButton>
                </EditableStyleWrapper>
              )}

              {!!settings.settingsV1.storefront_twitter_link && (
                <EditableStyleWrapper path='footerDesktopSettings.socialMediaStyle'>
                  <IconButton>
                    <XIcon
                      sx={{
                        color: secondary,
                        ...settings.settingsV2?.footerDesktopSettings
                          ?.socialMediaStyle,
                      }}
                    />
                  </IconButton>
                </EditableStyleWrapper>
              )}

              {!!settings.settingsV1.storefront_instagram_link && (
                <EditableStyleWrapper path='footerDesktopSettings.socialMediaStyle'>
                  <IconButton>
                    <InstagramIcon
                      sx={{
                        color: secondary,
                        ...settings.settingsV2?.footerDesktopSettings
                          ?.socialMediaStyle,
                      }}
                    />
                  </IconButton>
                </EditableStyleWrapper>
              )}

              {!!settings.settingsV1.storefront_youtube_link && (
                <EditableStyleWrapper path='footerDesktopSettings.socialMediaStyle'>
                  <IconButton>
                    <YouTubeIcon
                      sx={{
                        color: secondary,
                        ...settings.settingsV2?.footerDesktopSettings
                          ?.socialMediaStyle,
                      }}
                    />
                  </IconButton>
                </EditableStyleWrapper>
              )}

              {!!settings.settingsV1.storefront_linkedin_link && (
                <EditableStyleWrapper path='footerDesktopSettings.socialMediaStyle'>
                  <IconButton>
                    <LinkedInIcon
                      sx={{
                        color: secondary,
                        ...settings.settingsV2?.footerDesktopSettings
                          ?.socialMediaStyle,
                      }}
                    />
                  </IconButton>
                </EditableStyleWrapper>
              )}
            </Box>
          </Box>

          <PopularCategoriesMenu />

          <FooterMenu
            title={settings.settingsV1.storefront_footer_menu_one_title}
            menuItems={settings.footer1}
          />
          <FooterMenu
            title={settings.settingsV1.storefront_footer_menu_two_title}
            menuItems={settings.footer2}
          />
        </Box>

        <EditableStyleWrapper path='footerDesktopSettings.dividerStyle'>
          <Divider
            color={secondary}
            sx={{
              width: bodyWidth,
              ...settings.settingsV2?.footerDesktopSettings?.dividerStyle,
            }}
          />
        </EditableStyleWrapper>

        <EditableStyleWrapper path='footerDesktopSettings.copyrightStyle'>
          <Typography
            sx={{
              ...textStyles,
              ...settings.settingsV2?.footerDesktopSettings?.copyrightStyle,
            }}
          >
            © 2025 NNET E-Ticaret. Tüm hakları saklıdır.
          </Typography>
        </EditableStyleWrapper>
      </Box>
    </EditableStyleWrapper>
  );
};
